import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { FaShield, FaQrcode, FaCopy, FaApple, FaGooglePlay } from "react-icons/fa6";
import { setupTOTP, verifyTOTP, generateBackupCodes } from "../api/auth.js";
import { addToast } from "../store/app/action.js";
import BackupCodesModal from "./BackupCodesModal.jsx";

function TOTPSetupModal({ isOpen, onClose, onSuccess }) {
  const { user } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verification
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodesModalOpen, setBackupCodesModalOpen] = useState(false);


  // Call the setup API when modal opens
  useEffect(() => {
    const initializeTOTP = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await setupTOTP();
        
        if (response.status === "success") {
          // Generate QR code URL for Google Authenticator
          const baseIssuer = "Saucey Dashboard";
          const issuer = response.app_env !== "production" 
            ? `${baseIssuer} - ${response.app_env}` 
            : baseIssuer;
          const accountName = user?.email || "User";
          const secret = response.secret;
          const qrCodeUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
          
          setSetupData({
            ...response,
            qrCodeUrl,
            accountName,
            issuer
          });
        } else {
          setError(response.message || "Failed to initialize TOTP setup.");
        }
      } catch (err) {
        setError("Failed to initialize TOTP setup. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && !setupData && !loading) {
      initializeTOTP();
    }
  }, [isOpen]);

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      // TODO: Add toast notification for successful copy
    } catch (err) {
    }
  };

  const handleClose = () => {
    setSetupData(null);
    setError(null);
    setStep(1);
    setVerificationCode("");
    setLoading(false);
    setBackupCodesModalOpen(false);
    onClose();
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    try {
      setVerifying(true);
      setError(null);
      
      const response = await verifyTOTP(verificationCode);
      
      if (response.status === "success") {
        // Update token if provided
        if (response.token) {
          localStorage.setItem("token", response.token);
        }
        
        // Update user data in Redux if provided
        if (response.user) {
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: response,
          });
          
          // Check if user has no backup codes and generate them
          if (!response.user.mfa?.backup_codes || response.user.mfa.backup_codes.length === 0) {
            setBackupCodesModalOpen(true);
            return; // Don't call handleSuccess yet, wait for backup codes
          }
        }
        
        handleSuccess();
      } else {
        setError(response.message || "Invalid verification code");
      }
    } catch (err) {
      if (err.response && err.response.status === 400) {
        setError("Invalid TOTP code. Please check your authenticator app and try again.");
      } else {
        setError("Verification failed. Please check your code and try again.");
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSuccess = () => {
    setSetupData(null);
    setError(null);
    setStep(1);
    setVerificationCode("");
    if (onSuccess) {
      onSuccess();
    }
  };

  
  return (
    <ModalBasic
      id="totp-setup-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      width={"2xl"}
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 mx-auto mb-4">
            <FaShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
            Set Up Authenticator App
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center">
            Scan the QR code with your authenticator app to set up two-factor authentication.
          </p>
        </div>

        {/* Download App Buttons */}
        <div className="mb-6">
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-3">
            Don't have an authenticator app? Download Google Authenticator:
          </p>
          <div className="flex justify-center space-x-3">
            <a
              href="https://apps.apple.com/us/app/google-authenticator/id388497605"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <FaApple className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">App Store</span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US&pli=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FaGooglePlay className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Google Play</span>
            </a>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-500 dark:text-slate-400">Setting up TOTP...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        {setupData && step === 1 && (
          <div className="space-y-6">
            {/* QR Code Section */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qrCodeUrl)}`}
                  alt="TOTP QR Code"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Scan this QR code with your authenticator app
              </p>
            </div>

            {/* Manual Entry Option */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
              <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                Can't scan? Enter manually:
              </h4>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-sm bg-white dark:bg-slate-700 p-2 rounded border font-mono break-all">
                  {setupData.secret}
                </code>
                <button
                  onClick={copySecret}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  title="Copy secret key"
                >
                  <FaCopy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Account: {setupData.accountName} | Issuer: {setupData.issuer}
              </p>
            </div>

            {/* Instructions */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">Setup Instructions:</h3>
              <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-decimal list-inside">
                <li>Open your authenticator app (Google Authenticator, Authy, etc.)</li>
                <li>Tap "Add Account" or the "+" button</li>
                <li>Scan the QR code above</li>
                <li>Enter the 6-digit code from your app to verify</li>
              </ol>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton onClick={handleClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => setStep(2)}>
                I've Scanned the Code
              </PrimaryButton>
            </div>
          </div>
        )}

        {setupData && step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                Verify Your Setup
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            {/* Verification Code Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  maxLength="6"
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setVerificationCode(value);
                  }}
                  className="form-input w-full text-center text-2xl tracking-widest"
                  autoComplete="off"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton onClick={() => setStep(1)}>
                Back
              </SecondaryButton>
              <PrimaryButton 
                onClick={verifyCode}
                disabled={verifying || verificationCode.length !== 6}
              >
                {verifying ? "Verifying..." : "Verify & Complete Setup"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
      
      {/* Backup Codes Modal */}
      <BackupCodesModal
        isOpen={backupCodesModalOpen}
        onClose={() => setBackupCodesModalOpen(false)}
        onSuccess={() => {
          setBackupCodesModalOpen(false);
          handleSuccess();
        }}
      />
    </ModalBasic>
  );
}

export default TOTPSetupModal;
